import { useState } from "react";
import { useSetPublic } from "../../hooks/useSetPublic";
import { S3Tags } from "./types";
import { useI18n } from "../../i18n";

type PublicShareControlProps = {
  videoKey: string;
  tags: S3Tags[];
};

const isPublicTag = (tags: S3Tags[]): boolean =>
  tags.some((tag) => tag.Key === "Public" && tag.Value === "true");

// Admin-only: toggle a video's public flag and copy its /p/{slug} link.
export default function PublicShareControl({
  videoKey,
  tags,
}: PublicShareControlProps) {
  const { t } = useI18n();
  const { setPublic, isLoading, error } = useSetPublic();
  const [isPublic, setIsPublic] = useState(() => isPublicTag(tags));
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const toggle = async () => {
    const next = !isPublic;
    const url = await setPublic(videoKey, next);
    if (url === null) return; // request failed; leave the toggle unchanged
    setIsPublic(next);
    setLink(next ? url : null);
    setCopied(false);
  };

  // Already public on load: re-publish (idempotent) just to get the slug back.
  const revealLink = async () => {
    const url = await setPublic(videoKey, true);
    if (url) setLink(url);
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable; the field stays selectable for manual copy.
    }
  };

  return (
    <div className="border-t border-base-200 pt-2 mt-1">
      <label className="flex items-center justify-between gap-2 cursor-pointer">
        <span className="text-xs font-medium">{t("publicShare.label")}</span>
        <input
          type="checkbox"
          className="toggle toggle-primary toggle-sm"
          checked={isPublic}
          disabled={isLoading}
          onChange={toggle}
        />
      </label>

      {isPublic && (
        <div className="mt-2">
          {link ? (
            <div className="join w-full">
              <input
                type="text"
                readOnly
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                aria-label={t("publicShare.linkAria")}
                className="input input-xs input-bordered join-item flex-1"
              />
              <button
                onClick={copyLink}
                className="btn btn-xs join-item btn-primary"
              >
                {copied ? t("common.copied") : t("common.copy")}
              </button>
            </div>
          ) : (
            <button
              onClick={revealLink}
              disabled={isLoading}
              className="btn btn-xs btn-outline"
            >
              {t("publicShare.show")}
            </button>
          )}
          <p className="text-xs opacity-50 mt-1">{t("publicShare.info")}</p>
        </div>
      )}

      {error && (
        <p className="text-error text-xs mt-1">{t("publicShare.error")}</p>
      )}
    </div>
  );
}
